import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
});

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface OrderSummaryCardStyles {
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    poNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: InjectableCss;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    dataCell: {
        css: css`
            &&& {
                a span {
                    color: ${getColor("common.ui1")};
                    font-weight: bold;
                    &:hover,
                    &:focus {
                        color: ${getColor("primary.main")};
                    }
                }
            }
        `,
    },
};

const OrderSummaryCard: FC<Props> = ({ language, order, orderSettings, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    const orderNumber = order.erpOrderNumber || order.webOrderNumber;

    return (
        <>
            <DataTableCell
                {...styles.dataCell}
                data-th={translate("Order Date")}
                data-test-selector="orderSummaryCard_Date"
                className="order-date-grid-item"
            >
                {orderDateDisplay}
            </DataTableCell>
            <DataTableCell translate="no" {...styles.dataCell} data-th={translate("Order Number")} className="order-number-grid-item">
                {orderNumber && <OrderDetailPageTypeLink title={orderNumber} orderNumber={orderNumber} />}
            </DataTableCell>
            <DataTableCell translate="no" {...styles.dataCell} data-th={translate("PO Number")} className="order-po-grid-item">
                {order.customerPO && (
                    <SmallHeadingAndText
                        heading=""
                        text={order.customerPO}
                        extendedStyles={styles.poNumberHeadingAndText}
                    />
                )}
            </DataTableCell>
            <DataTableCell {...styles.dataCell} data-th={translate("Status")} className="order-status-grid-item">
                {order.statusDisplay && (
                    <SmallHeadingAndText
                        heading=""
                        text={order.statusDisplay}
                        extendedStyles={styles.statusHeadingAndText}
                    />
                )}
            </DataTableCell>
            <DataTableCell translate="no" {...styles.dataCell} data-th={translate("Total")} className="order-total-grid-item">
                {order.orderGrandTotalDisplay && (
                    <SmallHeadingAndText
                        heading=""
                        text={order.orderGrandTotalDisplay}
                        extendedStyles={styles.totalHeadingAndText}
                    />
                )}
            </DataTableCell>
        </>
    );
};

export default connect(mapStateToProps)(OrderSummaryCard);
